// src/components/jobDetails/SurveyUploadModal.jsx
import React, { useState, useMemo, useEffect } from "react";
import { toast } from "react-hot-toast";
import API from "../../api/api";

export default function SurveyUploadModal({ 
  selectedSurveyRun, 
  uploadedFiles, 
  onFileUpload, 
  onClose, 
  onSubmit,
  jobId,
  processing // Add processing prop from parent to disable button during upload
}) {
  const [surveyTypes, setSurveyTypes] = useState([]);
  // Remove local loading state, use processing from parent

  const surveyTypeMap = useMemo(() => {
    const map = {};
    surveyTypes.forEach(st => {
      map[st.survey_type_id] = st.survey_type;
    });
    return map;
  }, [surveyTypes]);

  useEffect(() => {
    async function fetchSurveyTypes() {
      try {
        const response = await API.get('surveytype/');
        setSurveyTypes(response.data);
      } catch (error) {
        console.error('Failed to load survey types:', error);
      }
    }
    fetchSurveyTypes();
  }, []);

  const handleFileUpload = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      onFileUpload(prev => ({ ...prev, [fileType]: file }));
    }
  };

  const handleSubmit = () => {
    if (!selectedSurveyRun || !selectedSurveyRun.survey_type) return;

    // Check if jobId is available
    if (!jobId) {
      toast.error('Job ID is missing. Cannot upload files.');
      console.error('Job ID is undefined. Please check the parent component.');
      return;
    }

    const surveyTypeId = selectedSurveyRun.survey_type;

    // Validate files based on survey type
    if (surveyTypeId === 1 || surveyTypeId === 2) {
      if (!uploadedFiles.inrun) {
        toast.error('Please upload an Excel file');
        return;
      }
    } else {
      if (!uploadedFiles.inrun || !uploadedFiles.outrun) {
        toast.error('Please upload both Excel files');
        return;
      }
    }

    // Call the parent's onSubmit function with the uploaded files
    onSubmit(uploadedFiles);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-8 w-full max-w-xl">
        <h3 className="text-lg font-semibold mb-4">
          Upload Survey Data for Run #{selectedSurveyRun.run_number}
        </h3>
        
        {/* Debug info - you can remove this in production */}
        {!jobId && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p className="text-sm">Warning: Job ID is missing. Upload may fail.</p>
          </div>
        )}
        
        <div className="mb-4">
          {selectedSurveyRun.survey_type && (
            <p className="text-sm text-gray-600 mb-2">
              Survey Type: {surveyTypeMap[selectedSurveyRun.survey_type] || 'Unknown'}
            </p>
          )}
          
          {selectedSurveyRun.survey_type ? (
            (selectedSurveyRun.survey_type === 1 || selectedSurveyRun.survey_type === 2) ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Excel File
                </label>
                <input 
                  type="file" 
                  accept=".xlsx, .xls"
                  onChange={(e) => handleFileUpload(e, 'inrun')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {uploadedFiles.inrun && (
                  <p className="text-xs text-green-600 mt-1">
                    {uploadedFiles.inrun.name} uploaded
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload Inrun Excel File
                  </label>
                  <input 
                    type="file" 
                    accept=".xlsx, .xls"
                    onChange={(e) => handleFileUpload(e, 'inrun')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {uploadedFiles.inrun && (
                    <p className="text-xs text-green-600 mt-1">
                      {uploadedFiles.inrun.name} uploaded
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload Outrun Excel File
                  </label>
                  <input 
                    type="file" 
                    accept=".xlsx, .xls"
                    onChange={(e) => handleFileUpload(e, 'outrun')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {uploadedFiles.outrun && (
                    <p className="text-xs text-green-600 mt-1">
                      {uploadedFiles.outrun.name} uploaded
                    </p>
                  )}
                </div>
              </div>
            )
          ) : (
            <p className="text-red-500 text-sm">
              Survey type not defined for this run
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={processing || !jobId} // Use processing prop from parent
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
          >
            {processing ? 'Uploading...' : 'Upload'} {/* Change text to Upload */}
          </button>
        </div>
      </div>
    </div>
  );
}